// AliasCombobox — 别名筛选下拉，带搜索
// F59：InboxPage 高级筛选用；固定拉 top 100 活跃别名 + 前端子串过滤
// 支持"全部别名"选项（value=null）

import { Check, ChevronDown, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAliases } from '../lib/aliases.js';
import { cn } from '../lib/cn.js';

interface Props {
  value: number | null;
  onChange: (next: number | null) => void;
  /** 显示尺寸；InboxPage 用 md */
  size?: 'sm' | 'md';
}

export function AliasCombobox({ value, onChange, size = 'md' }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  // 无参数（filter={}）拉 top 100 活跃别名；useAliases 自己 staleTime=60s
  const q = useAliases({ status: 'active' });
  const items = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter((a) => a.localPart.toLowerCase().includes(s));
  }, [items, search]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = value != null ? items.find((a) => a.id === value) : null;
  const displayLabel = selected
    ? selected.localPart
    : value != null
      ? `#${String(value)}`
      : '全部别名';

  const heightCls = size === 'sm' ? 'h-8 text-xs' : 'h-9 text-sm';
  const widthCls = size === 'sm' ? 'w-40' : 'w-52';

  return (
    <div ref={rootRef} className={cn('relative', widthCls)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex w-full items-center justify-between gap-1 rounded-lg border border-slate-200 bg-white px-2.5 outline-none focus:border-brand-400 dark:border-slate-700 dark:bg-slate-950',
          heightCls,
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="min-w-0 flex-1 truncate text-left">{displayLabel}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-30 mt-1 w-full rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
          <div className="relative mb-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索 local part..."
              className="h-8 w-full rounded-md border border-slate-200 bg-slate-50 pl-7 pr-2 text-xs outline-none focus:border-brand-400 focus:bg-white dark:border-slate-700 dark:bg-slate-950"
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
                setSearch('');
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-slate-100 dark:hover:bg-slate-800',
                value == null ? 'bg-brand-50 dark:bg-brand-500/10' : '',
              )}
            >
              <span className="flex-1 whitespace-nowrap">全部别名</span>
              {value == null ? <Check className="h-3.5 w-3.5 text-brand-500" /> : null}
            </button>
            {q.isLoading ? (
              <div className="py-3 text-center text-xs text-slate-500 whitespace-nowrap">
                加载别名…
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-3 text-center text-xs text-slate-500 whitespace-nowrap">
                无匹配别名
              </div>
            ) : (
              filtered.map((a) => {
                const on = a.id === value;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      onChange(a.id);
                      setOpen(false);
                      setSearch('');
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-slate-100 dark:hover:bg-slate-800',
                      on ? 'bg-brand-50 dark:bg-brand-500/10' : '',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{a.localPart}</span>
                    <span className="shrink-0 text-[10px] text-slate-400 whitespace-nowrap">
                      {a.source === 'gmail_plus' ? 'Gmail+' : 'CF'}
                    </span>
                    {on ? (
                      <Check className="h-3.5 w-3.5 shrink-0 text-brand-500" />
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
